/**
 * Admin · Output Governance — edit the exact, brand-/legal-critical text that is
 * injected into every generated assessment: the disclaimer (rule 9), the score
 * caveat (rule 10), and the closing call-to-action steps (rule 11).
 * Blank fields fall back to the hardcoded defaults. Changes apply to NEW reports.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Loader2, Save, RotateCcw, Plus, Trash2 } from "lucide-react";

export default function GovernancePanel() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [defaults, setDefaults] = useState(null);
  const [form, setForm] = useState({ disclaimer: "", score_caveat: "", cta_heading: "", cta_items: [] });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/governance");
      setDefaults(data.defaults);
      const e = data.effective;
      setForm({
        disclaimer: e.disclaimer || "",
        score_caveat: e.score_caveat || "",
        cta_heading: e.cta_heading || "",
        cta_items: (e.cta_items || []).map((i) => ({ title: i.title || "", description: i.description || "" })),
      });
    } catch (e) {
      toast.error("Could not load governance settings.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/governance", form);
      toast.success("Governance text saved. Applies to new assessments.");
      const e = data.effective;
      setForm({
        disclaimer: e.disclaimer || "",
        score_caveat: e.score_caveat || "",
        cta_heading: e.cta_heading || "",
        cta_items: (e.cta_items || []).map((i) => ({ title: i.title || "", description: i.description || "" })),
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  const resetField = (field) => {
    if (!defaults) return;
    setForm((f) => ({ ...f, [field]: defaults[field] }));
  };
  const resetCta = () => defaults && setForm((f) => ({ ...f, cta_heading: defaults.cta_heading, cta_items: defaults.cta_items.map((i) => ({ ...i })) }));

  const updateItem = (idx, key, val) =>
    setForm((f) => ({ ...f, cta_items: f.cta_items.map((it, i) => (i === idx ? { ...it, [key]: val } : it)) }));
  const addItem = () => setForm((f) => ({ ...f, cta_items: [...f.cta_items, { title: "", description: "" }] }));
  const removeItem = (idx) => setForm((f) => ({ ...f, cta_items: f.cta_items.filter((_, i) => i !== idx) }));

  if (loading) {
    return <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /> Loading…</div>;
  }

  const field = (key, label, rows, helper) => (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 p-5" data-testid={`governance-${key}`}>
      <div className="flex items-center justify-between">
        <p className="font-medium text-slate-900 dark:text-slate-50">{label}</p>
        <button onClick={() => resetField(key)} className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 dark:hover:text-slate-200" data-testid={`reset-${key}`}>
          <RotateCcw className="h-3.5 w-3.5" /> Reset to default
        </button>
      </div>
      {helper && <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{helper}</p>}
      <textarea
        value={form[key]}
        onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
        rows={rows}
        data-testid={`governance-${key}-input`}
        className="mt-3 w-full rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 p-3 text-sm text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-teal-500/30"
      />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="governance-panel">
      <div>
        <h2 className="font-display text-xl font-semibold text-slate-900 dark:text-slate-50">Output Governance</h2>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
          Controls the exact disclaimer, score caveat, and closing call-to-action injected into every generated
          assessment. Leave a field blank to use the built-in default. Changes apply to new assessments.
        </p>
      </div>

      {field("disclaimer", "Disclaimer (shown first, before all content)", 5)}
      {field("score_caveat", "Score caveat (shown next to readiness scores)", 4)}

      <div className="rounded-xl border border-slate-200 dark:border-slate-800 p-5" data-testid="governance-cta">
        <div className="flex items-center justify-between">
          <p className="font-medium text-slate-900 dark:text-slate-50">Closing call-to-action (final block)</p>
          <button onClick={resetCta} className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 dark:hover:text-slate-200" data-testid="reset-cta">
            <RotateCcw className="h-3.5 w-3.5" /> Reset to default
          </button>
        </div>
        <input
          value={form.cta_heading}
          onChange={(e) => setForm((f) => ({ ...f, cta_heading: e.target.value }))}
          placeholder="Heading"
          data-testid="governance-cta-heading"
          className="mt-3 w-full rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 h-10 text-sm text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-teal-500/30"
        />
        <div className="mt-4 space-y-3">
          {form.cta_items.map((it, idx) => (
            <div key={idx} className="rounded-md border border-slate-200 dark:border-slate-800 p-3" data-testid={`cta-item-edit-${idx}`}>
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-teal-700">{idx + 1}</span>
                <input
                  value={it.title}
                  onChange={(e) => updateItem(idx, "title", e.target.value)}
                  placeholder="Step title"
                  data-testid={`cta-item-${idx}-title`}
                  className="flex-1 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 h-9 text-sm text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-teal-500/30"
                />
                <button onClick={() => removeItem(idx)} className="text-slate-400 hover:text-red-600" data-testid={`cta-item-${idx}-remove`}><Trash2 className="h-4 w-4" /></button>
              </div>
              <textarea
                value={it.description}
                onChange={(e) => updateItem(idx, "description", e.target.value)}
                rows={2}
                placeholder="Description"
                data-testid={`cta-item-${idx}-desc`}
                className="mt-2 w-full rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 p-2 text-sm text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-teal-500/30"
              />
            </div>
          ))}
        </div>
        <button onClick={addItem} className="mt-3 inline-flex items-center gap-1.5 text-sm text-teal-700 hover:text-teal-600" data-testid="cta-add-item">
          <Plus className="h-4 w-4" /> Add step
        </button>
      </div>

      <button
        onClick={save}
        disabled={saving}
        data-testid="governance-save-btn"
        className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-slate-900 dark:bg-teal-600 text-white text-sm font-medium hover:bg-slate-800 dark:hover:bg-teal-500 disabled:opacity-50"
      >
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save governance text
      </button>
    </div>
  );
}
